import React from 'react';
import { X, Mail, Phone, Calendar, Shield, MapPin } from '../icons';
import type { User } from './types';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

/**
 * Profile Modal - displays user information
 * Unified component for all modules
 */
export const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose, user }) => {
  if (!isOpen || !user) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getInitials = () => {
    const firstName = user.imie || '';
    const lastName = user.nazwisko || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Nigdy';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50" 
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: '#ffffff', zIndex: 10000 }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10" style={{ backgroundColor: '#ffffff' }}>
          <h2 className="text-lg font-bold text-gray-900">Mój profil</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Profile Header Card */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" style={{ backgroundColor: '#f9fafb' }}>
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white text-xl font-semibold">
                  {getInitials()}
                </div>
              </div>

              {/* User Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-gray-900">
                  {user.imie} {user.nazwisko}
                </h3>
                {user.role_names && user.role_names.length > 0 && (
                  <p className="text-sm text-gray-600">
                    {user.role_names.join(', ')}
                  </p>
                )}
                {user.region_names && user.region_names.length > 0 && (
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {user.region_names.join(', ')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Personal Information Card */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" style={{ backgroundColor: '#f9fafb' }}>
            <h3 className="text-base font-bold text-gray-900 mb-3">Informacje osobiste</h3>
            
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 block">Imię</label>
                <p className="text-sm font-semibold text-gray-900">{user.imie || '-'}</p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 block">Nazwisko</label>
                <p className="text-sm font-semibold text-gray-900">{user.nazwisko || '-'}</p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 block flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  E-mail
                </label>
                <p className="text-sm font-semibold text-gray-900">{user.email || '-'}</p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 block">Login</label>
                <p className="text-sm font-semibold text-gray-900">{user.login || '-'}</p>
              </div>

              {user.telefon && (
                <div>
                  <label className="text-xs font-medium text-gray-500 block flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    Telefon
                  </label>
                  <p className="text-sm font-semibold text-gray-900">{user.telefon}</p>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-gray-500 block flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Ostatnie logowanie
                </label>
                <p className="text-sm font-semibold text-gray-900">{formatDate(user.ostatnie_logowanie)}</p>
              </div>
            </div>
          </div>

          {/* Security & Status Card */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" style={{ backgroundColor: '#f9fafb' }}>
            <h3 className="text-base font-bold text-gray-900 mb-3">Status i bezpieczeństwo</h3>
            
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 block flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  Status konta
                </label>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  user.aktywny 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {user.aktywny ? 'Aktywne' : 'Nieaktywne'}
                </span>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 block">2FA</label>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  user.mfa_enabled 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {user.mfa_enabled ? 'Włączone' : 'Wyłączone'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 flex justify-end z-10" style={{ backgroundColor: '#ffffff' }}>
          <button
            onClick={onClose}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;

